/* Zenflow Active remodel — behaviour for zf-* sections.
   Loaded as an ES module from layout/theme.liquid so it can reuse the theme's
   standard cart events (same mechanism the theme's own product forms use). */
import { CartLinesUpdateEvent } from '@shopify/events';

const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Scroll reveals ---------- */
let io = null;
function observeReveals(root = document) {
  const els = root.querySelectorAll('.zf-reveal:not(.is-in)');
  if (!('IntersectionObserver' in window) || reduceMotion || document.documentElement.classList.contains('shopify-design-mode')) {
    els.forEach((el) => el.classList.add('is-in'));
    return;
  }
  if (!io) {
    io = new IntersectionObserver(
      (entries) => {
        for (const en of entries) {
          if (en.isIntersecting) {
            en.target.classList.add('is-in');
            io.unobserve(en.target);
          }
        }
      },
      { rootMargin: '0px 0px 10% 0px', threshold: 0.01 }
    );
  }
  els.forEach((el) => io.observe(el));
}

/* ---------- Quick add (single-variant products) ---------- */
async function quickAdd(form) {
  const button = form.querySelector('[data-zf-quick-add]');
  if (!button || button.disabled) return;
  const variantId = form.querySelector('input[name="id"]')?.value;
  if (!variantId) return;

  const idle = button.dataset.labelIdle || button.textContent;
  button.disabled = true;
  button.textContent = button.dataset.labelAdding || 'Adding…';

  const formData = new FormData(form);
  // Ask Shopify to re-render the cart drawer / cart page sections in the same request,
  // exactly like the theme's own product form does.
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));
  formData.append('sections_url', window.location.pathname);

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: Number(formData.get('quantity') || 1) }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const response = await res.json();
    if (!res.ok || response.status) throw new Error(response.description || response.message || 'Could not add to cart');

    const ajaxCart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'zf-quick-add',
        sourceId: form.id,
        itemCount: Number(formData.get('quantity') || 1),
        productId: form.dataset.productId,
        sections: response.sections,
        didError: false,
      },
    });

    button.textContent = button.dataset.labelAdded || 'Added ✓';
    button.classList.add('is-added');
    setTimeout(() => {
      button.textContent = idle;
      button.classList.remove('is-added');
      button.disabled = false;
    }, 1600);
  } catch (err) {
    deferred.reject(err);
    button.textContent = button.dataset.labelError || 'Try again';
    button.disabled = false;
    console.error('[zf-quick-add]', err);
  }
}

function bindQuickAdd(root = document) {
  root.querySelectorAll('form[data-zf-quick-add-form]').forEach((form) => {
    if (form.dataset.zfBound) return;
    form.dataset.zfBound = '1';
    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      quickAdd(form);
    });
  });
}

/* ---------- Newsletter inline feedback ---------- */
function bindNewsletter(root = document) {
  root.querySelectorAll('.zf-form[data-zf-newsletter]').forEach((form) => {
    if (form.dataset.zfBound) return;
    form.dataset.zfBound = '1';
    // Native POST to /contact (customer form) — Shopify redirects back with ?customer_posted=true.
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true' && form.dataset.successMessage) {
      const msg = form.querySelector('.zf-form__msg');
      if (msg) {
        msg.textContent = form.dataset.successMessage;
        msg.classList.add('zf-form__msg--ok');
      }
    }
  });
}

function init(root = document) {
  observeReveals(root);
  bindQuickAdd(root);
  bindNewsletter(root);
}

init();

/* Theme editor: re-run when a section is (re)loaded so new markup gets bound. */
document.addEventListener('shopify:section:load', (ev) => init(ev.target));
document.addEventListener('shopify:block:select', (ev) => {
  ev.target?.closest('.zf-reveal')?.classList.add('is-in');
});
